import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  Device,
  TransformationService,
  LmapService,
  TestAssembleService,
  TestServerConfigService,
  SchedulerFormat,
  TestMode
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Subject } from 'rxjs';
import { FtpConfigFieldMapping } from './models/models';

@Injectable({
  providedIn: 'root'
})
export class FtpSetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  tabDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  testTaskName = 'ftp';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  tenantId = 'pass.local';
  controllerConfig = null;
  tcpPorts = [];
  defaultTcpPort = [{ name: 'First available', value: 0 }];
  private getControllerConfig = '/uicomm/getControllerConfiguration';
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  getAllTestParams(): any {
    this.output['engine-a-info'] = this.getEngineAInfo();
    if (this.output[FtpConfigFieldMapping.TEST_MODE] !== TestMode.MA_TO_EXTERNAL) {
      this.output['engine-b-info'] = this.getEngineBInfo();
    }
    this.output['ftp-parameters'] = this.getFtpTestConfig();
    return this._getFormatOutput();
  }

  getEngineAInfo(): any {
    const engineInfo = this.output[FtpConfigFieldMapping.ENGINE_A_INFO];
    const engineAInfo = {};
    if (engineInfo) {
      engineAInfo['address'] = engineInfo['address'];
      engineAInfo['engine-type'] =
        this.output[FtpConfigFieldMapping.SELECTED_ORIGINATOR].getModel();
      engineAInfo['port'] = engineInfo['port'];
      engineAInfo['test-ip'] = engineInfo['test-ip'];
      engineAInfo['test-subnet-mask'] = engineInfo['test-subnet-mask'];
      engineAInfo['test-gateway'] = engineInfo['test-gateway'];
      delete engineAInfo['agent-type'];
    }
    return engineAInfo;
  }

  getEngineBInfo(): any {
    const engineInfo = this.output[FtpConfigFieldMapping.ENGINE_B_INFO];
    const engineBInfo = {};
    if (engineInfo) {
      engineBInfo['address'] = engineInfo['address'];
      engineBInfo['engine-type'] =
        this.output[FtpConfigFieldMapping.SELECTED_DESTINATION].getModel();
      engineBInfo['port'] = engineInfo['port'];
      engineBInfo['test-ip'] = engineInfo['test-ip'];
      engineBInfo['test-subnet-mask'] = engineInfo['test-subnet-mask'];
      engineBInfo['test-gateway'] = engineInfo['test-gateway'];
      delete engineBInfo['agent-type'];
    }
    return engineBInfo;
  }

  getFtpTestConfig(): any {
    const testconfig = {};
    testconfig['mode'] = this.output[FtpConfigFieldMapping.TEST_MODE];
    testconfig['direction'] = this.output[FtpConfigFieldMapping.DIRECTION];
    testconfig['type'] = this.output[FtpConfigFieldMapping.TEST_TYPE];
    testconfig['tcp-port'] = this.output[FtpConfigFieldMapping.TCP_PORT];
    testconfig['threshold-enabled'] = this.output[FtpConfigFieldMapping.THRESHOLD_ENABLED];
    testconfig['engine-a-to-b-url'] = '';
    testconfig['engine-b-to-a-url'] = '';
    testconfig['engine-a-to-b-threshold'] = '';
    testconfig['engine-b-to-a-threshold'] = '';
    testconfig['target-enabled'] = this.output[FtpConfigFieldMapping.TARGET_ENABLED];
    testconfig['engine-a-to-b-target'] = '';
    testconfig['engine-b-to-a-target'] = '';
    if (testconfig['mode'] === TestMode.MA_TO_EXTERNAL) {
      if (testconfig['direction'] !== TestMode.DOWNLOAD) {
        testconfig['engine-a-to-b-url'] = this.output[FtpConfigFieldMapping.ENGINE_A_TO_B_URL];
      }
      if (testconfig['direction'] !== TestMode.UPLOAD) {
        testconfig['engine-b-to-a-url'] = this.output[FtpConfigFieldMapping.ENGINE_B_TO_A_URL];
      }
    }
    if (testconfig['threshold-enabled']) {
      if (testconfig['direction'] !== TestMode.DOWNLOAD) {
        testconfig['engine-a-to-b-threshold'] =
          this.output[FtpConfigFieldMapping.ENGINE_A_TO_B_THRESHOLD];
      }
      if (testconfig['direction'] !== TestMode.UPLOAD) {
        testconfig['engine-b-to-a-threshold'] =
          this.output[FtpConfigFieldMapping.ENGINE_B_TO_A_THRESHOLD];
      }
    }
    if (testconfig['target-enabled']) {
      if (testconfig['direction'] !== TestMode.DOWNLOAD) {
        testconfig['engine-a-to-b-target'] =
          this.output[FtpConfigFieldMapping.ENGINE_A_TO_B_TARGET];
      }
      if (testconfig['direction'] !== TestMode.UPLOAD) {
        testconfig['engine-b-to-a-target'] =
          this.output[FtpConfigFieldMapping.ENGINE_B_TO_A_TARGET];
      }
    }
    /* fixed value for now*/
    testconfig['max-test-duration'] = 60;
    testconfig['bdp-table-version'] = 1;
    return testconfig;
  }

  //eslint-disable-next-line
  createLMAPObject(dataModel: any): any {
    const testPointA = this.output[FtpConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[FtpConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    testPointA['id'] = testPointA.uniqueId;
    if (testPointB) {
      testPointB['id'] = testPointB.uniqueId;
    }
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    if (!this.output.scheduler?.repeatInterval) {
      this.output.runType = 'on-demand';
    }
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }

  //eslint-disable-next-line
  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj: SchedulerFormat = { ...this.output[FtpConfigFieldMapping.SCHEDULER] };
    // create the test schedule
    const testScheduleName = this.output[FtpConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.timeStamp && !schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['viavi-solutions-ftp:ftp'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };
    const actionOption2 = {
      id: 'role',
      value: 'primary'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption2);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    const tags = this.output.scheduler?.repeatInterval ? tagOptions : null;
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);
    this.lmapService.CONST.resultFormatType = 'v2';
    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // rt results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'rt',
      collectorURL,
      schedObj.repeatInterval,
      schedObj.timeStamp,
      null
    );
    return lmapified;
  }

  //eslint-disable-next-line
  createControllerConfig(testPointA, testPointB, lmapified): any {
    let controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    if (this.output[FtpConfigFieldMapping.TEST_MODE] !== TestMode.MA_TO_EXTERNAL) {
      const secondaryConfig = JSON.parse(JSON.stringify(lmapified));
      let foundRole = false,
        foundPhyId = false;
      // secondary config is created as a copy of primary config, so need to update for role and phy-id
      for (const x in secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0]
        .option) {
        // update role to secondary
        if (
          !foundRole &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'role'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = 'secondary';
          foundRole = true;
        }
        // remove phy-id object if secondary device doesn't have multiple ports
        if (!foundPhyId && !testPointB.phyId) {
          if (
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x]
              .id === 'phy-id'
          ) {
            foundPhyId = true;
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.splice(
              x,
              1
            );
          }
        }

        // set the value of phy-id
        if (
          !foundPhyId &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'phy-id'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = testPointB.phyId ? testPointB.phyId : ''; // set value if exists, blank otherwise
          foundPhyId = true;
        }
        if (foundRole && foundPhyId) {
          break;
        }
      }

      // case when primary device is single port, so need to add phy-id object for secondary config
      if (!foundPhyId) {
        if (testPointB.phyId) {
          const optionObjIndex =
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.length;
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ] = {};
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].id = 'phy-id';
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].value = testPointB.phyId;
        }
      }
      controllerConfig = this.testAssembleService.addSecondaryTestConfig(
        controllerConfig,
        'secondaryConfig',
        testPointB,
        this.testTaskName,
        secondaryConfig
      );
    }
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    controllerConfig.test['test-list-data']['mode'] = this.output[FtpConfigFieldMapping.TEST_MODE];
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output[FtpConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output[FtpConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output[FtpConfigFieldMapping.TECHINICIAN],
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[FtpConfigFieldMapping.ALIAS],
      comments : this.output.comments
    };
    controllerConfig.test['extended-config']['test'] = {
      mode: this.output[FtpConfigFieldMapping.TEST_MODE],
      nameOnProbe: 'ftp',
      selected: true,
      testType: 'FTP'
    };
    controllerConfig.test['extended-config']['direction'] = {
      name: this.output[FtpConfigFieldMapping.DIRECTION]
    };
    return controllerConfig;
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  getControllerConfiguration(): void {
    const url = `${this.getControllerConfig}`;
    this.controllerConfig = this.apiService.post$<any>(url);
  }

  getScheduleTimeStamp(timestamp: string): string {
    if (timestamp.toLowerCase() === 'now') {
      return null; //To start the test immediately -Need to update based on backend value
    } else {
      const currentTimestamp = new Date().getTime();
      if (Number(timestamp) < currentTimestamp) {
        return null;
      } else {
        return new Date(Number(timestamp)).toISOString();
      }
    }
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }
  getOriginatorAgent(): any {
    return this.selectedOriginator;
  }

  setDestinationAgent(device: Device): void {
    this.selectedDestination = device;
  }

  setTCPPorts(ports: Array<string>): void {
    let tcpPorts = [];
    tcpPorts = Object.assign([], this.defaultTcpPort);
    ports.forEach(port => tcpPorts.push({ name: port, value: parseInt(port) }));
    this.tcpPorts = tcpPorts;
  }
}
